import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { api } from '../../api/CheckApi';
import './TemplateDesigner.css';

// Bu alan adları ve backend'e gönderilen X/Y anahtarları bilinçli olarak korunmuştur.
const INITIAL_POSITIONS = {
  company_title: { x: 50, y: 50 },
  tax_number: { x: 50, y: 100 },
  tax_office: { x: 200, y: 100 },
  location: { x: 350, y: 50 },
  date: { x: 550, y: 50 },
  amount: { x: 550, y: 100 },
  amount_text: { x: 50, y: 150 },
};

const FIELDS = [
  { key: 'company_title', label: 'Firma Ünvanı', detail: 'Çekin düzenlendiği firma', color: '#2563eb', preview: 'Firma Ünvanı' },
  { key: 'tax_number', label: 'Vergi No', detail: 'Vergi kimlik numarası', color: '#e11d48', preview: 'Vergi No' },
  { key: 'tax_office', label: 'Vergi Dairesi', detail: 'Firmanın kayıtlı vergi dairesi', color: '#ea580c', preview: 'Vergi Dairesi' },
  { key: 'location', label: 'Yer', detail: 'Çekin düzenlendiği yer', color: '#7c3aed', preview: 'Yer' },
  { key: 'date', label: 'Tarih', detail: 'Çek üzerindeki tarih alanı', color: '#059669', preview: 'Tarih' },
  { key: 'amount', label: 'Tutar', detail: 'Rakamla çek tutarı', color: '#334155', preview: '# Tutar #' },
  { key: 'amount_text', label: 'Yazıyla Tutar', detail: 'Tutarın yazıyla karşılığı', color: '#a16207', preview: 'Tutar (Yazıyla)' },
];

const ICON_PATHS = {
  layout: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 9v12"/></>,
  layers: <><path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5M3 17l9 5 9-5"/></>,
  save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><path d="M17 21v-8H7v8M7 3v5h8"/></>,
  plus: <path d="M12 5v14M5 12h14"/>,
  pencil: <><path d="m16 5 3 3M4 20l4.5-1 11-11a2.12 2.12 0 0 0-3-3l-11 11L4 20Z"/></>,
  trash: <><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M5 6l1 15h12l1-15M10 10v7M14 10v7"/></>,
  info: <><circle cx="12" cy="12" r="10"/><path d="M12 11v5M12 8h.01"/></>,
  grip: <><circle cx="9" cy="5" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="19" r="1"/></>,
  move: <><path d="m8 4 4-4 4 4M12 0v24M8 20l4 4 4-4M4 8l-4 4 4 4M0 12h24M20 8l4 4-4 4"/></>,
  x: <path d="M18 6 6 18M6 6l12 12"/>,
  check: <path d="m4 12 5 5L20 6"/>,
  alert: <><path d="M10.3 3.9 1.8 19a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v5M12 18h.01"/></>,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6M8 13h8M8 17h8"/></>,
  refresh: <><path d="M20 7v5h-5M4 17v-5h5"/><path d="M5 9a8 8 0 0 1 14-2l1 5M4 12l1 5a8 8 0 0 0 14-2"/></>,
};

function Icon({ name, size = 18, className = '' }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name] || ICON_PATHS.info}
    </svg>
  );
}

const safeCoordinate = (value, fallback) => {
  const parsed = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(parsed)
    ? parsed
    : fallback;
};

const TemplateDesigner = () => {
  const [templateName, setTemplateName] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [positions, setPositions] = useState(INITIAL_POSITIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false, type: 'info', title: '', message: '', onConfirm: null,
  });

  // Hooks her render'da aynı sırayla çağrılır. react-draggable nodeRef gereksinimi korunur.
  const nodeRefs = {
    company_title: useRef(null),
    tax_number: useRef(null),
    tax_office: useRef(null),
    location: useRef(null),
    date: useRef(null),
    amount: useRef(null),
    amount_text: useRef(null),
  };

  const loadTemplates = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const data = await api.getTemplates();
      setSavedTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Şablonlar yüklenemedi:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  const handleStop = (key, _event, ui) => {
    setPositions((prev) => ({ ...prev, [key]: { x: ui.x, y: ui.y } }));
  };

  const closeModal = () => {
    setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
  };
  const showMessage = (type, title, message) => {
    setModal({ isOpen: true, type, title, message, onConfirm: null });
  };
  const showConfirm = (title, message, onConfirm) => {
    setModal({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const handleEdit = (template) => {
    setEditingId(template.id);
    setTemplateName(template.template_name || '');
    setPositions({
      company_title: {
        x: safeCoordinate(template.company_title_x, INITIAL_POSITIONS.company_title.x),
        y: safeCoordinate(template.company_title_y, INITIAL_POSITIONS.company_title.y),
      },
      tax_number: {
        x: safeCoordinate(template.tax_number_x, INITIAL_POSITIONS.tax_number.x),
        y: safeCoordinate(template.tax_number_y, INITIAL_POSITIONS.tax_number.y),
      },
      tax_office: {
        x: safeCoordinate(template.tax_office_x, INITIAL_POSITIONS.tax_office.x),
        y: safeCoordinate(template.tax_office_y, INITIAL_POSITIONS.tax_office.y),
      },
      location: {
        x: safeCoordinate(template.location_x, INITIAL_POSITIONS.location.x),
        y: safeCoordinate(template.location_y, INITIAL_POSITIONS.location.y),
      },
      date: {
        x: safeCoordinate(template.date_x, INITIAL_POSITIONS.date.x),
        y: safeCoordinate(template.date_y, INITIAL_POSITIONS.date.y),
      },
      amount: {
        x: safeCoordinate(template.amount_x, INITIAL_POSITIONS.amount.x),
        y: safeCoordinate(template.amount_y, INITIAL_POSITIONS.amount.y),
      },
      amount_text: {
        x: safeCoordinate(template.amount_text_x, INITIAL_POSITIONS.amount_text.x),
        y: safeCoordinate(template.amount_text_y, INITIAL_POSITIONS.amount_text.y),
      },
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setTemplateName('');
    setPositions(INITIAL_POSITIONS);
  };

  const handleSave = async () => {
    const name = templateName.trim();
    if (!name) return showMessage('info', 'Eksik bilgi', 'Lütfen şablona bir isim veriniz.');
    if (saving) return;

    // API sözleşmesi değiştirilmedi: aynı alanlar, aynı anahtarlar.
    const payload = {
      template_name: name,
      company_title_x: positions.company_title.x,
      company_title_y: positions.company_title.y,
      tax_number_x: positions.tax_number.x,
      tax_number_y: positions.tax_number.y,
      tax_office_x: positions.tax_office.x,
      tax_office_y: positions.tax_office.y,
      location_x: positions.location.x,
      location_y: positions.location.y,
      date_x: positions.date.x,
      date_y: positions.date.y,
      amount_x: positions.amount.x,
      amount_y: positions.amount.y,
      amount_text_x: positions.amount_text.x,
      amount_text_y: positions.amount_text.y,
    };

    setSaving(true);
    try {
      if (editingId !== null) {
        await api.updateTemplate(editingId, payload);
        showMessage('success', 'Şablon güncellendi', 'Değişikliklerin başarıyla kaydedildi.');
      } else {
        await api.createTemplate(payload);
        showMessage('success', 'Şablon oluşturuldu', 'Yeni çek şablonu başarıyla kaydedildi.');
      }
      await loadTemplates();
      handleCancelEdit();
    } catch (error) {
      showMessage('error', 'Kayıt başarısız', `İşlem tamamlanamadı: ${error?.response?.data?.detail || error?.message || 'Bilinmeyen hata'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    showConfirm('Şablon silinsin mi?', 'Bu şablon kalıcı olarak silinecek. İşleme devam etmek istiyor musun?', async () => {
      if (deleting) return;
      setDeleting(true);
      try {
        await api.deleteTemplate(id);
        closeModal();
        await loadTemplates();
        if (editingId === id) handleCancelEdit();
      } catch (error) {
        showMessage('error', 'Silme başarısız', error?.response?.data?.detail || error?.message || 'Şablon silinemedi.');
      } finally {
        setDeleting(false);
      }
    });
  };

  return (
    <div className="ctd-root">
      <section className="ctd-hero">
        <div className="ctd-hero-content">
          <div className="ctd-eyebrow"><Icon name="layers" size={14}/> FİNANS & ÇEK / ŞABLON YÖNETİMİ</div>
          <h1>Çek Şablon Tasarımcısı</h1>
          <p>Çek üzerindeki bilgi alanlarını sürükleyerek konumlandır, şablonunu kaydet ve dilediğinde düzenle.</p>
        </div>
        <div className="ctd-hero-stats" aria-label="Çalışma alanı bilgileri">
          <div><span>Çalışma alanı</span><strong>800 × 350</strong><small>piksel</small></div>
          <div><span>Konumlandırılabilir</span><strong>07</strong><small>bilgi alanı</small></div>
          <div><span>Kayıtlı şablon</span><strong>{savedTemplates.length.toLocaleString('tr-TR')}</strong><small>tasarım</small></div>
        </div>
      </section>

      <section className="ctd-card ctd-editor-card" aria-labelledby="ctd-editor-title">
        <div className="ctd-section-header">
          <div className="ctd-section-title-group">
            <span className="ctd-section-icon"><Icon name="layout" size={19}/></span>
            <div>
              <h2 id="ctd-editor-title">Şablon Düzenleyici</h2>
              <p>Şablon adını belirle ve aşağıdaki çalışma alanında yerleşimi düzenle.</p>
            </div>
          </div>
          <span className={`ctd-state-chip ${editingId !== null ? 'is-editing' : 'is-new'}`}>
            <span className="ctd-state-dot"/>
            {editingId !== null ? 'Düzenleme modu' : 'Yeni şablon'}
          </span>
        </div>

        <div className="ctd-formbar">
          <div className="ctd-name-field">
            <label htmlFor="ctd-template-name">Şablon adı</label>
            <input
              id="ctd-template-name"
              type="text"
              placeholder="Örn: Ziraat Bankası Çek Şablonu"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleSave(); }}
            />
          </div>
          <div className="ctd-action-group">
            {editingId !== null && (
              <button type="button" className="ctd-btn ctd-btn-secondary" onClick={handleCancelEdit} disabled={saving}>
                <Icon name="x" size={16}/> Vazgeç / Yeni
              </button>
            )}
            <button type="button" className="ctd-btn ctd-btn-primary" onClick={handleSave} disabled={saving}>
              <Icon name="save" size={17}/>
              {saving ? 'Kaydediliyor...' : editingId !== null ? 'Güncellemeyi Kaydet' : 'Şablonu Kaydet'}
            </button>
          </div>
        </div>

        <div className="ctd-workspace-grid">
          <div className="ctd-workspace-main">
            <div className="ctd-canvas-toolbar">
              <span className="ctd-canvas-label"><span className="ctd-live-dot"/> CANLI YERLEŞİM ALANI</span>
              <span className="ctd-canvas-help"><Icon name="move" size={14}/> Alanları fareyle sürükle</span>
            </div>

            <div className="ctd-canvas-viewport" aria-label="800 x 350 piksel çek şablonu tasarım alanı">
              <div className="ctd-check-paper">
                <div className="ctd-paper-topline" aria-hidden="true">
                  <span>ÇEK FORMU • ŞABLON ÖNİZLEME</span>
                  <span>SMS GRUP</span>
                </div>
                <div className="ctd-paper-watermark" aria-hidden="true">SMS</div>
                <div className="ctd-paper-footer" aria-hidden="true">
                  <span>Çek yazdırma alanı</span><span>800 × 350 px</span>
                </div>

                {FIELDS.map((field) => (
                  <Draggable
                    key={field.key}
                    nodeRef={nodeRefs[field.key]}
                    position={positions[field.key]}
                    onStop={(event, ui) => handleStop(field.key, event, ui)}
                    bounds="parent"
                  >
                    <div
                      ref={nodeRefs[field.key]}
                      className="ctd-drag-field"
                      style={{ '--field-color': field.color }}
                      title={`${field.label} – sürükleyerek taşı`}
                    >
                      <span className="ctd-drag-field-handle"><Icon name="grip" size={13}/></span>
                      {field.preview}
                    </div>
                  </Draggable>
                ))}
              </div>
            </div>
            <div className="ctd-canvas-footnote"><Icon name="info" size={16}/> Yerleşim ölçüleri ve kayıtlı koordinatlar değiştirilmedi. Küçük ekranlarda alanı yatay kaydırabilirsin.</div>
          </div>

          <aside className="ctd-fields-panel" aria-label="Alan listesi ve koordinatlar">
            <div className="ctd-fields-head">
              <h3>Bilgi Alanları</h3>
              <span>7 alan</span>
            </div>
            <p className="ctd-fields-description">Alanların renkleri ve mevcut X/Y konumları</p>
            <div className="ctd-fields-list">
              {FIELDS.map((field) => (
                <div className="ctd-field-item" key={field.key}>
                  <span className="ctd-field-marker" style={{ backgroundColor: field.color }}/>
                  <div className="ctd-field-content">
                    <strong>{field.label}</strong>
                    <small>{field.detail}</small>
                  </div>
                  <div className="ctd-position-pill" title="X ve Y koordinatları">
                    {Math.round(positions[field.key].x)}, {Math.round(positions[field.key].y)}
                  </div>
                </div>
              ))}
            </div>
            <div className="ctd-tip"><Icon name="info" size={17}/><span>Taşıma işlemi bittiğinde koordinatlar otomatik güncellenir. Kalıcı olması için <strong>Şablonu Kaydet</strong> butonunu kullan.</span></div>
          </aside>
        </div>
      </section>

      <section className="ctd-card ctd-saved-card" aria-labelledby="ctd-saved-title">
        <div className="ctd-section-header">
          <div className="ctd-section-title-group">
            <span className="ctd-section-icon ctd-section-icon-green"><Icon name="file" size={19}/></span>
            <div>
              <h2 id="ctd-saved-title">Kayıtlı Şablonlar</h2>
              <p>Önceden oluşturulmuş çek şablonlarını yönet.</p>
            </div>
          </div>
          <button type="button" className="ctd-btn ctd-btn-light ctd-refresh-btn" onClick={loadTemplates} disabled={loading}>
            <Icon name="refresh" size={16}/> Yenile
          </button>
        </div>

        {loading ? (
          <div className="ctd-empty"><span className="ctd-empty-icon"><Icon name="refresh" size={23}/></span><h3>Şablonlar yükleniyor</h3><p>Kayıtlı şablonlar getiriliyor.</p></div>
        ) : fetchError ? (
          <div className="ctd-empty"><span className="ctd-empty-icon ctd-empty-error"><Icon name="alert" size={23}/></span><h3>Şablonlar alınamadı</h3><p>Sunucu bağlantısını kontrol edip tekrar deneyebilirsin.</p><button type="button" className="ctd-btn ctd-btn-secondary" onClick={loadTemplates}>Tekrar Dene</button></div>
        ) : savedTemplates.length === 0 ? (
          <div className="ctd-empty"><span className="ctd-empty-icon"><Icon name="layers" size={25}/></span><h3>Henüz kayıtlı şablon yok</h3><p>İlk şablonunu yukarıdaki tasarım alanından oluşturabilirsin.</p></div>
        ) : (
          <div className="ctd-template-list">
            {savedTemplates.map((template, index) => (
              <div key={template.id} className={`ctd-template-row ${editingId === template.id ? 'is-selected' : ''}`}>
                <div className="ctd-template-icon"><Icon name="file" size={20}/></div>
                <div className="ctd-template-info">
                  <div className="ctd-template-title">
                    <strong>{template.template_name || 'İsimsiz şablon'}</strong>
                    {editingId === template.id && <span className="ctd-editing-badge">Düzenleniyor</span>}
                  </div>
                  <p>Şablon #{template.id ?? index + 1} · 7 konumlandırılabilir alan</p>
                </div>
                <div className="ctd-template-actions">
                  <button type="button" className="ctd-btn ctd-btn-edit" onClick={() => handleEdit(template)}><Icon name="pencil" size={15}/> Düzenle</button>
                  <button type="button" className="ctd-btn ctd-btn-delete" onClick={() => handleDelete(template.id)}><Icon name="trash" size={15}/> Sil</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {modal.isOpen && (
        <div className="ctd-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && modal.type !== 'confirm') closeModal(); }}>
          <div className="ctd-modal" role="dialog" aria-modal="true" aria-labelledby="ctd-modal-title" aria-describedby="ctd-modal-desc">
            <div className={`ctd-modal-icon ctd-modal-${modal.type}`}>
              <Icon name={modal.type === 'success' ? 'check' : modal.type === 'error' || modal.type === 'confirm' ? 'alert' : 'info'} size={25}/>
            </div>
            <h2 id="ctd-modal-title">{modal.title}</h2>
            <p id="ctd-modal-desc">{modal.message}</p>
            <div className="ctd-modal-actions">
              {modal.type === 'confirm' ? (
                <>
                  <button type="button" className="ctd-btn ctd-btn-secondary" onClick={closeModal} disabled={deleting}>İptal</button>
                  <button type="button" className="ctd-btn ctd-btn-danger" onClick={modal.onConfirm} disabled={deleting}>
                    <Icon name="trash" size={16}/>{deleting ? 'Siliniyor...' : 'Evet, Sil'}
                  </button>
                </>
              ) : (
                <button type="button" className="ctd-btn ctd-btn-primary ctd-modal-ok" onClick={closeModal}>Tamam</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateDesigner;
